import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkle, Heart, Shield, ArrowRight } from '@phosphor-icons/react';

const About = () => {
  const values = [
    {
      icon: Sparkle,
      title: 'Quality Selection',
      description: 'Every piece is meticulously chosen with attention to detail, ensuring premium quality that lasts.'
    },
    {
      icon: Heart,
      title: 'Affordable Elegance',
      description: 'We believe everyone deserves to shine. Our jewelry offers luxury aesthetics at accessible prices.'
    },
    {
      icon: Shield,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We provide exceptional service and support at every step.'
    }
  ];

  return (
    <div className="min-h-screen bg-background-default">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1693928876936-e6c25a7b79c4?w=1920)',
          }}
        >
          <div className="absolute inset-0 bg-black/60" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl"
          >
            <span className="inline-block font-body text-primary text-sm uppercase tracking-widest mb-4">
              Our Story
            </span>
            <h1 className="font-display text-4xl md:text-6xl text-white leading-tight mb-6">
              Where Elegance Meets <span className="italic text-primary">Affordability</span>
            </h1>
            <p className="font-body text-gray-300 text-lg leading-relaxed">
              Since our inception, Luster Adorn has been dedicated to bringing premium quality
              imitation jewelry to fashion-conscious individuals who appreciate beauty without compromise.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl md:text-4xl text-secondary mb-6">
                Crafting Dreams into Reality
              </h2>
              <div className="space-y-4 font-body text-text-secondary leading-relaxed">
                <p>
                  Luster Adorn was born from a simple belief: that beautiful jewelry should be accessible to everyone. Our founder, inspired by traditional Indian craftsmanship and contemporary design, set out to curate pieces that capture the essence of luxury.
                </p>
                <p>
                  We carefully select each piece in our collection from trusted sources to ensure it meets our high standards of premium quality imitation jewelry. These items are brought to you by skilled creators who bring decades of experience and passion to their work.

                </p>
                <p>
                  Today, Luster Adorn serves fashion-conscious customers across India, helping them express their unique style through our diverse collection of necklaces, earrings, bracelets, and rings.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1558882268-15aa056d885f?w=800"
                  alt="Elegant jewelry showcase"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-primary/10 -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-background-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl md:text-4xl text-secondary mb-4">Our Values</h2>
            <p className="font-body text-text-secondary max-w-xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 text-center"
              >
                <div className="w-16 h-16 bg-primary/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon size={32} className="text-primary" weight="light" />
                </div>
                <h3 className="font-display text-xl text-secondary mb-3">{value.title}</h3>
                <p className="font-body text-text-secondary text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <blockquote className="font-accent text-3xl md:text-4xl text-secondary italic leading-relaxed mb-6">
              "Jewelry is not just an accessory. It's a form of self-expression, 
              a way to tell your story without words."
            </blockquote>
            <p className="font-body text-primary uppercase tracking-widest text-sm">
              — The Luster Adorn Philosophy
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-3xl md:text-4xl text-white mb-6">
              Start Your Journey
            </h2>
            <p className="font-body text-gray-400 mb-8 max-w-xl mx-auto">
              Explore our collection and find the perfect piece that speaks to your style.
            </p>
            <Link to="/shop" className="btn-primary inline-flex items-center gap-2">
              Shop Now <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
